/**
 * RegularListContainer
 * --
 */
import React, { useState } from 'react';
import Presenter from './RegularListPresenter';
import MessageAlert, { POINT_STORE_ID } from 'utils';
import {
  useGetParcelServices,
  useUpdateParcelServiceStatusBulk,
  parcelServiceQueryKeys,
} from 'hooks/ParcelServiceHooks';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCreateRefundSubscriptionManual } from 'hooks/RefundHooks';
import {
  usePaymentCancelAccount,
  usePaymentCancelCard,
} from 'hooks/PaymentHooks.';

const today = {
  start_date: dayjs().subtract(7, 'day').startOf('day').unix(),
  end_date: dayjs().endOf('day').unix(),
  get_type: 'PAYMENT',
};
const initialSearchFilter = {
  keywordType: 'shop_order_id',
  keyword: '',
  getType: 'PAYMENT',
  dateType: 'week', // [today, week, month, 3month]
  startDate: null,
  endDate: null,
  start_date: today.start_date,
  end_date: today.end_date,
  ////
  keywordValue: '',
};

const options = {
  gcTime: Infinity,
  select: (res) => {
    const { status, data } = res;

    if (status !== 200) return [];
    else return data;
  },
  initialData: [],
};
/**----------------------------------------
 * [Component] 배송내역 - 컨테이너
 * ----------------------------------------
 */
const RegularListContainer = ({ defaultShopId, permission, type }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [period, setPeriod] = useState(today);
  const [currentShop, setCurrentShop] = useState(defaultShopId ?? null);

  /* ===== MUTATE ===== */
  const { updateParcelServiceStatusBulk } = useUpdateParcelServiceStatusBulk();

  const { cancelCard } = usePaymentCancelCard();
  const { cancelAccount } = usePaymentCancelAccount(); // 계좌이체 취소
  const { createRefundSubscriptionManual } =
    useCreateRefundSubscriptionManual();

  const refundMutate = {
    SUBSCRIPTION: createRefundSubscriptionManual,
  };
  /* ===== VARIABLE ===== */
  const { data: parcelServices } = useGetParcelServices(
    {
      type: 'SUBSCRIPTION',
      ...(currentShop && { shop_information_id: currentShop }),
      join_tables: `["user", "shop_information", "shop_order", "refund"]`,
      start_date: period.start_date,
      end_date: period.end_date,
      get_type: period.get_type,
    },
    {
      ...options,
      enabled: defaultShopId ? !!currentShop : true,
    }
  );

  const { data: parcelAllService } = useGetParcelServices(
    {
      type: 'SUBSCRIPTION',
      ...(currentShop && { shop_information_id: currentShop }),
      join_tables: `["user", "shop_information", "shop_order", "refund"]`,
      get_type: period.get_type,
    },
    {
      ...options,
      enabled: defaultShopId ? !!currentShop : true,
    }
  );
  /* ===== FUNCTION ===== */
  /**
   * 데이터 정제
   * --
   */
  const parcelAllServiceList = React.useMemo(() => {
    setLoading(true);
    try {
      let data = parcelAllService;
      if (searchFilter?.getType === 'CREATE') {
        data = data
          ?.map((parcel) => ({
            key: parcel.parcel_service_id,
            ...parcel,
          }))
          .filter((o) =>
            currentShop ? o.shop_information_id === currentShop : true
          )
          ?.sort((a, b) => b.parcel_service_id - a.parcel_service_id);
      } else {
        const mapParcelServiceList = new Map();
        ///
        data
          ?.map((parcel) => ({
            key: parcel.parcel_service_id,
            ...parcel,
          }))
          ?.filter((o) =>
            currentShop ? o.shop_information_id === currentShop : true
          )
          ?.forEach((parcel) => {
            const foundParcel = mapParcelServiceList.get(
              parcel?.parcel_service_id
            );
            if (foundParcel) {
              mapParcelServiceList.set(parcel?.parcel_service_id, {
                ...foundParcel,
                regular_payment_info: [
                  ...foundParcel.regular_payment_info,
                  parcel.regular_payment_info,
                ],
              });
            } else {
              mapParcelServiceList.set(parcel?.parcel_service_id, {
                ...parcel,
                regular_payment_info: [parcel.regular_payment_info],
                refund: parcel.refund ? [parcel.refund] : [],
              });
            }
          });
        ///
        data = Array.from(mapParcelServiceList.values())?.sort(
          (a, b) => b.parcel_service_id - a.parcel_service_id
        );
      }
      //환불금 계산
      const shopOrderMap = new Map();
      data?.forEach((parcel) => {
        const found = shopOrderMap.get(parcel?.shop_order_id);
        const refundAmount = parcel.refund.reduce(
          (acc, cur) => acc + (cur?.refund_amount || 0),
          0
        );
        if (found) {
          shopOrderMap.set(parcel?.shop_order_id, {
            refunded_amount: found.refunded_amount + refundAmount,
            product_price: found.product_price + parcel.payment_price,
          });
        } else {
          shopOrderMap.set(parcel?.shop_order_id, {
            refunded_amount: refundAmount,
            product_price: parcel.payment_price,
          });
        }
      });
      data = data.map((parcel) => {
        return {
          ...parcel,
          refunded_amount: shopOrderMap.get(parcel?.shop_order_id)
            ?.refunded_amount,
          product_price: shopOrderMap.get(parcel?.shop_order_id)?.product_price,
        };
      });
      const { keyword, keywordType } = searchFilter;
      const filteredCall = (o) => `${o[keywordType]}`.indexOf(keyword) >= 0;
      if (keywordType === 'shop_order_id') {
        data = data.filter(filteredCall);
      } else if (keywordType === 'product_name') {
        data = data.filter((o) => {
          return filteredCall(o['product']);
        });
      } else if (keywordType === 'user_name') {
        data = data.filter((o) => filteredCall(o['user']));
      }
      setLoading(false);
      return data;
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2500);
    }
  }, [parcelServices, searchFilter.keyword, currentShop]);
  /**
   * 데이터 정제
   * --
   */
  const parcelServiceList = React.useMemo(() => {
    setLoading(true);
    try {
      let data = parcelServices;
      if (searchFilter?.getType === 'CREATE') {
        data = data
          ?.map((parcel) => ({
            key: parcel.parcel_service_id,
            ...parcel,
          }))
          .filter((o) =>
            currentShop ? o.shop_information_id === currentShop : true
          )
          ?.sort((a, b) => b.parcel_service_id - a.parcel_service_id);
      } else {
        const mapParcelServiceList = new Map();
        ///
        data
          ?.map((parcel) => ({
            key: parcel.parcel_service_id,
            ...parcel,
          }))
          ?.filter((o) =>
            currentShop ? o.shop_information_id === currentShop : true
          )
          ?.forEach((parcel) => {
            const foundParcel = mapParcelServiceList.get(
              parcel?.parcel_service_id
            );
            if (foundParcel) {
              mapParcelServiceList.set(parcel?.parcel_service_id, {
                ...foundParcel,
                regular_payment_info: [
                  ...foundParcel.regular_payment_info,
                  parcel.regular_payment_info,
                ],
              });
            } else {
              mapParcelServiceList.set(parcel?.parcel_service_id, {
                ...parcel,
                regular_payment_info: [parcel.regular_payment_info],
                refund: parcel.refund ? [parcel.refund] : [],
              });
            }
          });
        ///
        data = Array.from(mapParcelServiceList.values())?.sort(
          (a, b) => b.parcel_service_id - a.parcel_service_id
        );
      }
      //환불금 계산
      const shopOrderMap = new Map();
      data?.forEach((parcel) => {
        const found = shopOrderMap.get(parcel?.shop_order_id);
        const refundAmount = parcel.refund.reduce(
          (acc, cur) => acc + (cur?.refund_amount || 0),
          0
        );
        if (found) {
          shopOrderMap.set(parcel?.shop_order_id, {
            refunded_amount: found.refunded_amount + refundAmount,
            product_price: found.product_price + parcel.payment_price,
          });
        } else {
          shopOrderMap.set(parcel?.shop_order_id, {
            refunded_amount: refundAmount,
            product_price: parcel.payment_price,
          });
        }
      });
      data = data.map((parcel) => {
        return {
          ...parcel,
          refunded_amount: shopOrderMap.get(parcel?.shop_order_id)
            ?.refunded_amount,
          product_price: shopOrderMap.get(parcel?.shop_order_id)?.product_price,
        };
      });
      const { keyword, keywordType } = searchFilter;
      const filteredCall = (o) => `${o[keywordType]}`.indexOf(keyword) >= 0;
      if (keywordType === 'shop_order_id') {
        data = data.filter(filteredCall);
      } else if (keywordType === 'product_name') {
        data = data.filter((o) => {
          return filteredCall(o['product']);
        });
      } else if (keywordType === 'user_name') {
        data = data.filter((o) => filteredCall(o['user']));
      }
      setLoading(false);
      return data;
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2500);
    }
  }, [parcelServices, searchFilter.keyword, currentShop]);
  /**--------------------
   * 필터
   * --------------------
   */
  const handleChange = (key, value) => {
    setSearchFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * 검색
   * --
   */
  const handleSearch = async (shopIdx = defaultShopId) => {
    // if (!currentShop) {
    //   return MessageAlert.info('매장을 선택해주세요.');
    // }

    const { dateType, startDate, endDate, keywordValue, getType } =
      searchFilter;

    const dd =
      dateType === 'today'
        ? 0
        : dateType === 'week'
        ? -7
        : dateType === 'month'
        ? -30
        : -90;

    setSearchFilter({
      ...searchFilter,
      keyword: keywordValue,
    });
    setCurrentShop(shopIdx);
    if (startDate && endDate) {
      setPeriod({
        start_date: startDate,
        end_date: endDate,
        get_type: getType,
      });
    } else {
      setPeriod({
        start_date: dayjs().add(dd, 'days').startOf('day').unix(),
        end_date: dayjs().endOf('day').unix(),
        get_type: getType,
      });
    }
  };
  /**
   * 검색 핌터 초기화
   * --
   */
  const handleResetFilter = () => {
    setPeriod(today);
    setSearchFilter(initialSearchFilter);
    setCurrentShop(defaultShopId);
  };

  /**
   * 데이터 최신화
   * --
   */
  const handleReload = () => {
    const queryKey = parcelServiceQueryKeys.getParcelServices({
      type: 'SUBSCRIPTION',
      ...(currentShop && { shop_information_id: currentShop }),
      join_tables: `["user", "shop_information", "shop_order", "refund"]`,
      start_date: period.start_date,
      end_date: period.end_date,
    });
    queryClient.invalidateQueries({ queryKey: queryKey.queryKey });
  };
  /**
   * 운송장 택배 저장
   * --
   */
  const handleSaveDeliveryInfo = async (data, callback = null) => {
    try {
      const { status } = await updateParcelServiceStatusBulk([data]);
      if (status === 200) {
        // NOTE: 데이터 최신화
        handleReload();

        MessageAlert.success('택배사/송장번호 등록 되었습니다.');
        callback && callback();
      }
    } catch (e) {
      MessageAlert.error('택배사/송장번호 등록 중 오류가 발생하였습니다!');
    }
  };

  /**--------------------
   * 주문 취소
   * --------------------
   * @param {object} refund 환불 정보
   * @param {string} type 결제 수단
   */
  const handleCancel = async (refund, type = 'CARD') => {
    /**NOTE - refund
     * trno[string]: payment_info의 trno (JM_PAY가 아닐 경우 필수)
     * cancelTaxFreeAmount[string | number]: 취소 면세금액
     * 예시) 총금액 1만원 부분취소 요청
            과세상품인 경우 refund_amount=10000, cancelTaxFreeAmount=0
            면세상품인 경우 refund_amount=10000, cancelTaxFreeAmount=10000
            일부(3천원) 면세상품인 경우 refund_amount=10000, cancelTaxFreeAmount=3000
     * cancelSeq: 취소 순서 (최초 1) refund가 일어난 횟수 + 1 (JM_PAY가 아닐 경우 필수)
     *
     * refund_amount[number]: 환불금액 (필수)
     * refund_num[number]: 환불수량 (필수)
     * is_coupon_refund[string]: 쿠폰환불 여부 (필수)
     * refund_reason[string]: 환불사유
     *
     * parcel_service_id[number]: 배송, 정기배송일 경우 필수
     * regular_payment_id: 정기배송일 경우 필수
     * rental_id: 렌탈일 경우 필수
     * product_type: DELIVERY | SUBSCRIPTION | RENTAL
     */
    let success = false;
    try {
      setLoading(true);
      const paymentType = type.toLocaleUpperCase();

      const {
        trno,
        cancelTaxFreeAmount,
        cancelSeq,
        product_type,
        refund_amount,
        ...rest
      } = refund;

      const cancelData = {
        mid: POINT_STORE_ID,
        orgTradeKeyType: 'TID',
        orgTradeKey: trno,
        cancelType: 'PARTIAL',
        cancelTotalAmount: `${refund_amount}`,
        cancelTaxFreeAmount: `${cancelTaxFreeAmount ?? 0}`,
        cancelSeq: `${cancelSeq ?? 1}`,
      };

      const refundData = {
        ...rest,
        memo: rest?.refund_reason,
        refund_amount,
        refund_status: 'COMPLETED',
      };

      let cancelled, refunded;
      switch (paymentType) {
        case 'MOBILE': // 휴대폰 결제
          MessageAlert.warning('휴대폰 결제는 취소 불가능합니다!');
          return false;
        case 'TRANSFER': // TODO: 계좌이체 (테스트 필요)
          // 계좌이체 결제 취소
          cancelled = await cancelAccount(cancelData);

          if (cancelled.status !== 200 || cancelled.data.code !== 'A0200') {
            MessageAlert.info('결제 취소에 실패하였습니다.');
            return false;
          }

          // 환불 데이터 생성
          refunded = await refundMutate[product_type]({
            ...refundData,
            refund_payment_info: cancelled.data.data,
          });
          break;
        case 'JM_PAY': // 제이엠페이 (테스트 필요)
          // JM페이 환불
          // const { status } = await refundChargeJmPay({
          //   user_id: rest?.user_id,
          //   price: refund_amount,
          // });

          // if (status !== 200) {
          //   MessageAlert.info('JM Pay 환불에 실패하였습니다.');
          //   return;
          // }

          // 환불 데이터 생성
          refunded = await refundMutate[product_type]({
            ...refundData,
            refund_payment_info: null,
          });

          break;
        case 'CARD':
        case 'NAVER_PAY':
        case 'KAKAO_PAY':
          // 카드 결제 취소
          cancelled = await cancelCard(cancelData);

          if (cancelled.status !== 200 || cancelled.data.code !== 'A0200') {
            MessageAlert.info('결제 취소에 실패하였습니다.');
            return false;
          }

          // 환불 데이터 생성
          refunded = await refundMutate[product_type]({
            ...refundData,
            refund_payment_info: cancelled.data.data,
          });

          break;
        default:
          MessageAlert.warning('알 수 없는 결제 타입!');
          return false;
      }

      if (refunded.status !== 200) {
        MessageAlert.info('환불 데이터 생성에 실패하였습니다.');
        return false;
      }

      MessageAlert.success('결제가 성공적으로 취소되었습니다.');
      success = true;
      return success;
    } catch (err) {
      console.error('[handleCancel] Error!! ', err);
      MessageAlert.error('결제 취소에 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setLoading(false);
        return success;
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */

  /* ===== RENDER ===== */
  return (
    <>
      <Presenter
        {...{
          loading,
          permission,
          type: 'RENTAL',
          parcelServiceList,
          parcelAllServiceList,
          setCurrentShop,
          searchFilter,
          currentShop,
          onChange: handleChange,
          onSearch: handleSearch,
          onReload: handleReload,
          onSaveDeliveryInfo: handleSaveDeliveryInfo,
          onResetFilter: handleResetFilter,
          onCancel: handleCancel,
        }}
      />
    </>
  );
};

/*  */
RegularListContainer.defaultProps = {
  defaultShopId: null,
  type: null,
  permission: 'ADMIN',
};
export default RegularListContainer;
